// Startup timing, reported back to the server.
//
// The sync being unusably slow was invisible for as long as it was: the client
// swallowed the failure in a catch and the server logged nothing, because
// nothing was wrong on the server. These measurements come from the only place
// that can see the real number -- the browser on the far end of the link.
//
// Deliberately not an analytics SDK. Sending a few hundred bytes to an endpoint
// this app already owns costs nothing to load, works offline-first, and keeps
// note titles and user identity out of a third party.

import feathersClient from '../feathers-client'

const steps = []

function payloadSize(result) {
  try {
    const rows = (result && result.data) || result
    return Array.isArray(rows) ? JSON.stringify(rows).length : 0
  } catch (e) {
    return 0
  }
}

// Wraps one step of the sync, recording how long it took and how much came back.
export async function timed(name, fn) {
  const started = performance.now()
  try {
    const result = await fn()
    steps.push({ name, ms: Math.round(performance.now() - started), bytes: payloadSize(result) })
    return result
  } catch (e) {
    steps.push({ name, ms: Math.round(performance.now() - started), error: e.message || String(e) })
    throw e
  }
}

export function rows(name, count) {
  const step = steps.find(s => s.name === name)
  if (step) step.count = count
}

// Fire and forget. A failure here must never affect the app, so everything is
// swallowed -- this is instrumentation, not a feature.
//
// This also carries the step errors. Production builds run
// babel-plugin-transform-remove-console, so a console.log in a catch block does
// not exist in the bundle a user runs: a failing sync was completely silent on
// both ends. Errors recorded by timed() come back here instead.
export async function report(extra = {}) {
  if (!steps.length) return
  const body = {
    steps: steps.splice(0, steps.length),
    total: extra.total,
    online: navigator.onLine,
    // effectiveType tells us whether a slow sync was the network or us.
    // Chromium only; undefined elsewhere, which is fine.
    connection: (navigator.connection || {}).effectiveType
  }
  try {
    const accessToken = await feathersClient.authentication.getAccessToken()
    if (!accessToken) return
    fetch('/timings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify(body),
      keepalive: true
    }).catch(() => {})
  } catch (e) { /* never let instrumentation break startup */ }
}
